import React, { useState } from 'react';
import { toast } from 'sonner';
import { X, Loader2, RefreshCw, Check } from 'lucide-react';
import { apiPost } from '../../lib/api';

const CUTOFF = '2026-10-01';
const todayIso = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
const ACTION_CLR = { ADD: 'text-emerald-700 bg-emerald-50', CHANGE: 'text-amber-700 bg-amber-50', REMOVE: 'text-rose-700 bg-rose-50' };

function PreviewTable({ changes }) {
  if (!changes.length) {
    return <div className="text-center py-6 text-sm text-slate-500" data-testid="co-recheck-no-changes">No changes needed — every comp off in this range is already correct.</div>;
  }
  return (
    <div className="max-h-[45vh] overflow-y-auto border border-slate-200 rounded" data-testid="co-recheck-table">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 sticky top-0">
          <tr className="text-[10px] uppercase tracking-widest text-slate-500">
            <th className="text-left p-2">Emp Code</th><th className="text-left p-2">Name</th><th className="text-left p-2">Date</th>
            <th className="text-left p-2">Day</th><th className="text-right p-2">Hours</th><th className="text-right p-2">Current</th>
            <th className="text-right p-2">New</th><th className="text-left p-2">Change</th>
          </tr>
        </thead>
        <tbody>
          {changes.map((c) => (
            <tr key={`${c.employee_id}-${c.date}`} className="border-t border-slate-100" data-testid={`co-recheck-row-${c.emp_code}-${c.date}`}>
              <td className="p-2 font-mono">{c.emp_code || '—'}</td>
              <td className="p-2">{c.employee_name}</td>
              <td className="p-2 tabular-nums">{c.date}</td>
              <td className="p-2">{c.day_kind === 'holiday' ? 'Holiday' : 'Sunday / weekly off'}</td>
              <td className="p-2 text-right tabular-nums">{c.hours ?? '—'}</td>
              <td className="p-2 text-right tabular-nums">{c.current_credit}</td>
              <td className="p-2 text-right tabular-nums font-bold">{c.new_credit}</td>
              <td className="p-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${ACTION_CLR[c.action]}`}>{c.action}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CompOffRecheckDialog({ onClose, onApplied }) {
  const [from, setFrom] = useState(CUTOFF);
  const [to, setTo] = useState(todayIso());
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (dryRun) => {
    setBusy(true);
    try {
      const r = await apiPost('/hr/comp-off/resync', null, { params: { from, to, dry_run: dryRun } });
      if (dryRun) setPreview(r);
      else {
        toast.success(`Comp offs updated: ${r.totals.add} added, ${r.totals.change} changed, ${r.totals.remove} removed`);
        onApplied();
        onClose();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Recheck failed');
    } finally { setBusy(false); }
  };
  const t = preview?.totals;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="co-recheck-dialog">
      <div className="bg-white rounded-md shadow-2xl w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div className="font-display font-bold">Recheck comp offs</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="co-recheck-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-xs text-slate-500">Re-applies the comp off rule (7 h or more = 1 day, 4 to under 7 h = 0.5 day) to every Sunday / weekly off and holiday in the range. Days before 01-Oct-2026 are never changed. The preview makes no changes.</p>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs text-slate-600">From
              <input type="date" min={CUTOFF} max={to} value={from} onChange={(e) => { setFrom(e.target.value); setPreview(null); }}
                className="block mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm" data-testid="co-recheck-from" />
            </label>
            <label className="text-xs text-slate-600">To
              <input type="date" min={from} max={todayIso()} value={to} onChange={(e) => { setTo(e.target.value); setPreview(null); }}
                className="block mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm" data-testid="co-recheck-to" />
            </label>
            <button onClick={() => run(true)} disabled={busy || !from || !to} data-testid="co-recheck-preview-btn"
              className="px-4 py-2 border border-[#3b4872] text-[#3b4872] rounded text-sm font-bold disabled:opacity-50 flex items-center gap-2">
              {busy && !preview ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Preview changes
            </button>
          </div>
          {preview && (
            <>
              <div className="flex flex-wrap gap-4 text-xs bg-slate-50 border border-slate-200 rounded p-3" data-testid="co-recheck-totals">
                <span><b className="text-emerald-700">{t.add}</b> to add</span>
                <span><b className="text-amber-700">{t.change}</b> to change</span>
                <span><b className="text-rose-700">{t.remove}</b> to remove</span>
                <span>Net <b>{t.net_days > 0 ? '+' : ''}{t.net_days}</b> days</span>
                <span className="text-slate-400">Preview only — nothing changed yet</span>
              </div>
              <PreviewTable changes={preview.changes} />
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-slate-300 rounded text-sm" data-testid="co-recheck-cancel">Cancel</button>
          <button onClick={() => run(false)} disabled={busy || !preview || !preview.changes.length} data-testid="co-recheck-apply-btn"
            className="px-4 py-2 bg-[#3b4872] text-white rounded text-sm font-bold disabled:opacity-40 flex items-center gap-2">
            {busy && preview ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Apply
          </button>
        </div>
      </div>
    </div>
  );
}
